/**
 * QA REAL SEND · el modo que permite observar un envio de verdad.
 *
 * NO es el modo demo, y mezclarlos fue el defecto que este modulo corrige. La
 * migracion 195 lo dejo escrito para `clientes` y la cotizacion se habia
 * quedado fuera:
 *
 *   demo    → EXPERIENCIA de demostracion. Datos falsos, caminos alternativos,
 *             efectos externos suprimidos.
 *   is_test → NATURALEZA del dato. Recorre el flujo REAL, con el mismo codigo y
 *             las mismas tablas. Solo se acotan los efectos peligrosos.
 *
 * Este interruptor declara lo segundo. Y por eso mismo es peligroso: una
 * cotizacion real que naciera QA por descuido se veria enviada y desapareceria
 * del pipeline, en silencio. Tres cosas lo acotan:
 *
 *   1. Vive en `sessionStorage`, NO en `localStorage`. Cerrar la pestaña lo
 *      apaga. El modo demo sobrevive a todo, y precisamente por eso no sirve
 *      como referencia aqui.
 *   2. Caduca solo a los 30 minutos. Nadie tiene un QA abierto media hora.
 *   3. Quien decide a donde puede salir un correo es el SERVIDOR
 *      (`QA_REAL_SEND_ALLOWLIST`). Lo de este archivo es aviso, no barrera.
 */

const CLAVE = 'feria_qa_envio_real';
export const DURACION_MS = 30 * 60 * 1000;

/**
 * QUIEN ENCIENDE EL MODO NO ES QUIEN PINTA LOS CANALES.
 *
 * El interruptor vive en `EditorV2Shell`; las casillas de WhatsApp y correo las
 * pinta el motor, que el shell recibe como `children`. React reutiliza ese
 * elemento cuando el shell cambia de estado, asi que el motor NO se vuelve a
 * renderizar y su lectura del modo se queda vieja: la banda decia ACTIVE y
 * WhatsApp seguia marcado y habilitado.
 *
 * Se midio en pantalla, no se dedujo. La barrera dura de `handleSend` aguantaba
 * igual —vuelve a preguntar en el clic— pero la interfaz invitaba a intentarlo.
 * Con esto el modo es observable y ambos lados leen lo mismo.
 */
const oyentes = new Set();

function avisar() {
  oyentes.forEach(fn => { try { fn(); } catch { /* un oyente roto no apaga el resto */ } });
}

/** Se suscribe a los cambios del modo. Devuelve la baja. */
export function suscribirQAEnvioReal(fn) {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

function almacen() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** ¿Esta activo AHORA? Caducado cuenta como apagado, y se limpia al preguntar. */
export function modoQAEnvioRealActivo(ahora = Date.now()) {
  const store = almacen();
  if (!store) return false;
  const hasta = Number(store.getItem(CLAVE) || 0);
  if (!hasta) return false;
  if (ahora >= hasta) {
    store.removeItem(CLAVE);
    return false;
  }
  return true;
}

/** Milisegundos que le quedan, para poder decirlo en pantalla. */
export function tiempoRestanteQA(ahora = Date.now()) {
  const store = almacen();
  if (!store) return 0;
  const hasta = Number(store.getItem(CLAVE) || 0);
  return hasta > ahora ? hasta - ahora : 0;
}

/** Enciende el modo por `DURACION_MS`. Volver a llamarlo renueva el plazo. */
export function activarQAEnvioReal(ahora = Date.now()) {
  const store = almacen();
  if (!store) return false;
  store.setItem(CLAVE, String(ahora + DURACION_MS));
  avisar();
  return true;
}

/** Apagado manual. */
export function desactivarQAEnvioReal() {
  const store = almacen();
  if (store) store.removeItem(CLAVE);
  avisar();
}

/**
 * Aviso PREVENTIVO, no seguridad.
 *
 * El front puede conocer una copia reducida de la lista para explicar el
 * problema antes de intentar el envio. Que este vacia no autoriza nada: el
 * servidor vuelve a decidir, y es el unico que decide.
 */
/**
 * ¿Este despliegue esta preparado para QA real?
 *
 * El control no aparece si no lo esta. Sin configuracion intencional, el modo
 * no deberia ni poder encenderse desde la interfaz: en produccion normal es
 * como si no existiera.
 *
 * Ojo: esto NO autoriza nada. Que el boton aparezca no significa que el correo
 * vaya a salir — eso lo decide `QA_REAL_SEND_ALLOWLIST` en el servidor.
 *
 * ── POR QUE UNA BANDERA PROPIA Y NO LA PISTA ───────────────────────────────
 *
 * Antes esto se derivaba de `REACT_APP_QA_SEND_HINT`: para que apareciera el
 * boton habia que meter un correo —el personal de alguien— en el bundle. Una
 * ayuda visual se habia convertido en el interruptor de la funcion, y el precio
 * de encenderla era publicar una direccion. Son dos cosas distintas y ahora se
 * declaran por separado: esta bandera decide si la herramienta EXISTE, la pista
 * es opcional y solo adorna el aviso.
 */
export function despliegueDeclaraQA() {
  return String(process.env.REACT_APP_QA_REAL_SEND_ENABLED || '').trim().toLowerCase() === 'true';
}

export function destinatariosQAConocidos() {
  return String(process.env.REACT_APP_QA_SEND_HINT || '')
    .split(/[,;\s]+/)
    .map(valor => valor.trim().toLowerCase())
    .filter(valor => valor.includes('@'));
}

/**
 * ¿Conviene avisar de que este destinatario no va a pasar?
 *
 * Devuelve `false` cuando no hay pista configurada: sin lista local no se puede
 * afirmar nada, y afirmar de mas seria peor que callar.
 */
export function destinatarioProbablementeNoAutorizado(email) {
  const conocidos = destinatariosQAConocidos();
  if (!conocidos.length) return false;
  return !conocidos.includes(String(email || '').trim().toLowerCase());
}


/**
 * LA IDENTIDAD PERSISTIDA GANA A LA SESION.
 *
 * El interruptor caduca a los 30 minutos; la fila no caduca nunca. Una
 * cotizacion guardada con `is_test = true` sigue siendo de prueba mañana,
 * cuando el modo lleva horas apagado. Si WhatsApp dependiera solo de la sesion,
 * bastaria con reabrir el borrador para que la casilla volviera a habilitarse.
 *
 * Por eso la pregunta no es «¿esta QA encendido?» sino «¿de que naturaleza es
 * esta cotizacion?». La sesion solo se suma para el caso en que todavia no hay
 * fila: lo que se cree ahora nacera de prueba.
 */
export function cotizacionEsQA(cotizacion) {
  return cotizacion?.is_test === true;
}

/** ¿Hay una fila de verdad detras, o solo el borrador en pantalla? */
export function cotizacionPersistida(cotizacion) {
  return Boolean(cotizacion?.id);
}

/**
 * ¿WhatsApp esta fuera de juego?
 *
 * QA REAL SEND V1 es SOLO CORREO. El correo tiene allowlist en el servidor;
 * WhatsApp no tiene a donde acotar el destino, asi que no se abre.
 */
export function whatsappBloqueadoPorQA({ sesionActiva = false, cotizacion = null } = {}) {
  return Boolean(sesionActiva) || cotizacionEsQA(cotizacion);
}

/** El texto de la casilla, que no es el mismo en los dos casos. */
export function motivoWhatsappBloqueado({ sesionActiva = false, cotizacion = null } = {}) {
  if (cotizacionEsQA(cotizacion)) return 'No disponible para cotizaciones QA';
  if (sesionActiva) return 'No disponible en QA Real Send';
  return '';
}

export const MOTIVO_QA_SOBRE_COTIZACION_REAL =
  'QA real debe activarse antes de crear o guardar la cotización. Esta propuesta ya pertenece al flujo real.';

/**
 * ENCENDER QA NO REETIQUETA NADA.
 *
 * El interruptor decide la NATURALEZA AL CREAR: `creacionEsDePrueba()` se lee
 * en el momento de construir la fila. Si la fila ya existe y nacio real, el
 * servidor la seguira viendo real —consulta `is_test` en la base, no cree al
 * navegador— y no le aplicara la allowlist. Encender QA ahi seria pintar una
 * banda de seguridad sobre una cotizacion que puede salir hacia cualquiera.
 *
 * La salida NO es hacer `UPDATE is_test = true`. Convertir filas reales en QA
 * es exactamente el movimiento que borra una cotizacion del pipeline en
 * silencio. Se bloquea la activacion y se dice por que.
 */
export function puedeActivarQASobre(cotizacion) {
  if (cotizacionPersistida(cotizacion) && !cotizacionEsQA(cotizacion)) {
    return { permitido: false, motivo: MOTIVO_QA_SOBRE_COTIZACION_REAL };
  }
  return { permitido: true, motivo: '' };
}
